import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, MessageSquare, CalendarClock, Stethoscope, Quote, FileText, LogOut, Menu, X, ExternalLink, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/data/site";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/admin/services", label: "Services", icon: Stethoscope },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/content", label: "Website Content", icon: FileText },
  { to: "/admin/security", label: "Security", icon: KeyRound },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const nav = (
    <nav className="flex flex-col gap-1 p-4">
      {LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          onClick={() => setOpen(false)}
          data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
              isActive ? "bg-terracotta text-cream" : "text-cream/70 hover:bg-cream/10 hover:text-cream"
            }`
          }
        >
          <l.icon className="h-4 w-4" /> {l.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col bg-charcoal lg:flex">
        <div className="flex items-center gap-3 border-b border-cream/10 p-5">
          <img src={ASSETS.logo} alt="logo" className="h-10 w-10 rounded-full object-contain" />
          <div>
            <p className="font-serif text-cream">Dr. Mithee's</p>
            <p className="text-[0.6rem] uppercase tracking-widest text-rose">Admin</p>
          </div>
        </div>
        {nav}
        <div className="mt-auto border-t border-cream/10 p-4">
          <Link to="/" target="_blank" className="mb-2 flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-cream/70 hover:bg-cream/10 hover:text-cream">
            <ExternalLink className="h-4 w-4" /> View Website
          </Link>
          <button data-testid="admin-logout" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-cream/70 hover:bg-cream/10 hover:text-cream">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-soft-border bg-charcoal p-4 lg:hidden">
        <div className="flex items-center gap-2">
          <img src={ASSETS.logo} alt="logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="font-serif text-cream">Admin</span>
        </div>
        <button data-testid="admin-mobile-toggle" onClick={() => setOpen((o) => !o)} className="text-cream">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="fixed inset-0 top-[57px] z-30 bg-charcoal lg:hidden">
          {nav}
          <div className="border-t border-cream/10 p-4">
            <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-cream/70"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      )}

      <main className="flex-1 px-5 pb-16 pt-20 lg:ml-64 lg:px-10 lg:pt-10">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
}
